import { useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { KeyRound, Wand2, Copy, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  const arr = new Uint32Array(12);
  window.crypto.getRandomValues(arr);
  const pw = Array.from(arr, (n) => chars[n % chars.length]).join("");
  return /\d/.test(pw) && /[a-z]/i.test(pw) ? pw : genPassword();
};

export const StaffPasswordCard = ({ userId }) => {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState("");

  const load = () => client.get(`/users/${userId}/password`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDialog = () => { setPw(""); setDone(""); setShow(true); setOpen(true); };
  const copy = (v) => navigator.clipboard?.writeText(v).then(() => toast.success("Copied"));

  const save = async () => {
    setSaving(true);
    try {
      await client.put(`/users/${userId}/password`, { password: pw });
      setDone(pw);
      toast.success("Password changed — they've been signed out on all devices");
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <Card className="p-5 border-slate-200" data-testid="staff-password-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-slate-800 flex items-center gap-1.5"><KeyRound className="h-4 w-4" />Password</h3>
          <p className="text-sm text-slate-600 mt-1 font-mono tracking-widest" data-testid="staff-password-mask">{info?.hasPassword ? "••••••••••" : "Not set"}</p>
          <p className="text-xs text-slate-500 mt-1" data-testid="staff-password-updated">
            {info?.passwordUpdatedAt ? `Last changed ${new Date(info.passwordUpdatedAt).toLocaleString()}` : "Never changed"}
          </p>
          <p className="text-[11px] text-slate-400 mt-1">Passwords are stored encrypted and can't be viewed — set a new one instead.</p>
        </div>
        <Button size="sm" variant="outline" onClick={openDialog} data-testid="staff-password-change-btn">Change password</Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="staff-password-dialog">
          <DialogHeader><DialogTitle>{done ? "Password changed" : "Set a new password"}</DialogTitle></DialogHeader>
          {done ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Share this with the staff member now — it won't be shown again. They've been signed out on all devices.</p>
              <div className="flex gap-2">
                <Input readOnly value={done} className="font-mono" data-testid="staff-password-result" />
                <Button variant="outline" onClick={() => copy(done)} data-testid="staff-password-copy-btn"><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>New password</Label>
              <div className="flex gap-2">
                <Input type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} className="font-mono" placeholder="At least 8 characters, a letter and a number" data-testid="staff-password-input" />
                <Button variant="outline" onClick={() => setShow(!show)} data-testid="staff-password-toggle-btn">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setPw(genPassword()); setShow(true); }} data-testid="staff-password-generate-btn"><Wand2 className="h-4 w-4 mr-1.5" />Generate strong password</Button>
            </div>
          )}
          <DialogFooter>
            {done ? (
              <Button onClick={() => setOpen(false)} data-testid="staff-password-done-btn">Done</Button>
            ) : (
              <Button onClick={save} disabled={saving || pw.length < 8} data-testid="staff-password-save-btn">{saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Save password</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
